/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * /invoices/$invoiceId — Invoice detail page (#27, #111).
 *
 * #27 B2: payment-link management (list, revoke, create → one-time reveal).
 * #111: balance summary (total / paid / due), payment history, record external payment.
 */
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Ban, Link2, PlusCircle, Trash2 } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  ConfirmDialog,
  InvoiceStatusBadge,
} from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { PaymentLinkRevealModal } from '@/components/ui/PaymentLinkRevealModal';
import {
  useInvoice,
  useVoidInvoice,
  useInvoicePayments,
  useRecordPayment,
} from '@/hooks/useInvoices';
import {
  usePaymentLinks,
  useCreatePaymentLink,
  useRevokePaymentLink,
} from '@/hooks/usePaymentLinks';
import { useAuthStore } from '@/stores/auth';
import { formatCurrency, formatDate, InvoiceStatus } from '@aelita/shared';
import type {
  PaymentLinkCreateResponse,
  PaymentLinkListItem,
  PaymentHistoryItem,
} from '@aelita/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalize a raw Decimal string from the API — may lack trailing zeros.
 * "700" → "700.00", "2691.67" → "2691.67"
 */
function normalizeDecimalString(raw: string): string {
  const n = parseFloat(raw);
  return isNaN(n) ? raw : n.toFixed(2);
}

/** Format a raw amount string as currency. */
function formatPaymentAmount(raw: string): string {
  const n = parseFloat(raw);
  return isNaN(n) ? raw : formatCurrency(n);
}

/** Map Payment.method (enum value) to human-readable label. */
const METHOD_LABEL: Record<string, string> = {
  card: 'Card',
  ach: 'ACH',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  other: 'External payment',
};

function methodLabel(method: string | null): string {
  return method ? (METHOD_LABEL[method] ?? method) : '—';
}

/** Map Payment.status to Badge variant. Reuses Badge — no duplicate status→color map. */
const PAYMENT_STATUS_BADGE: Record<
  string,
  { label: string; variant: 'green' | 'yellow' | 'red' | 'gray' }
> = {
  succeeded: { label: 'Succeeded', variant: 'green' },
  pending: { label: 'Pending', variant: 'yellow' },
  failed: { label: 'Failed', variant: 'red' },
  refunded: { label: 'Refunded', variant: 'gray' },
};

function PaymentStatusBadge({ status }: { status: string }) {
  const config = PAYMENT_STATUS_BADGE[status] ?? { label: status, variant: 'gray' as const };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export const Route = createFileRoute('/invoices/$invoiceId')({ component: InvoiceDetailPage });

// ---------------------------------------------------------------------------
// Payment link status → Badge variant (no duplicate map — Badge handles these)
// ---------------------------------------------------------------------------
const PAYMENT_LINK_BADGE: Record<
  PaymentLinkListItem['status'],
  { label: string; variant: 'green' | 'gray' | 'yellow' }
> = {
  active: { label: 'Active', variant: 'green' },
  expired: { label: 'Expired', variant: 'yellow' },
  used: { label: 'Used', variant: 'gray' },
};

// ---------------------------------------------------------------------------
// PaymentsSection
// ---------------------------------------------------------------------------

interface PaymentsSectionProps {
  invoiceId: string;
  isAdmin: boolean;
  balanceDue: string | undefined;
  invoiceStatus: string;
}

function PaymentsSection({ invoiceId, isAdmin, balanceDue, invoiceStatus }: PaymentsSectionProps) {
  const { data: payments, isLoading, isError } = useInvoicePayments(invoiceId);
  const recordPayment = useRecordPayment(invoiceId);

  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [recordError, setRecordError] = useState<string | null>(null);

  const balanceNum = balanceDue !== undefined ? parseFloat(balanceDue) : NaN;
  const canRecord =
    isAdmin && invoiceStatus !== InvoiceStatus.VOID && (isNaN(balanceNum) || balanceNum > 0);

  function openModal() {
    setAmount(balanceDue && !isNaN(balanceNum) ? normalizeDecimalString(balanceDue) : '');
    setPaidAt(new Date().toISOString().slice(0, 10));
    setRecordError(null);
    setShowModal(true);
  }

  async function handleRecord() {
    setRecordError(null);
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setRecordError('Amount must be greater than zero.');
      return;
    }
    try {
      await recordPayment.mutateAsync({
        amount: amountNum,
        method: 'other',
        paidAt: new Date(paidAt).toISOString(),
      });
      setShowModal(false);
    } catch (err: any) {
      const msg: string =
        err?.response?.data?.error?.message ?? err?.message ?? 'Failed to record payment.';
      setRecordError(msg);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Payments</CardTitle>
          {canRecord && (
            <Button
              size="sm"
              leftIcon={<PlusCircle className="h-4 w-4" />}
              onClick={openModal}
              aria-label="Record external payment"
            >
              Record payment
            </Button>
          )}
        </div>
      </CardHeader>

      {isLoading && (
        <CardBody>
          <div className="space-y-2 animate-pulse" aria-busy="true" aria-label="Loading payments">
            <div className="h-8 bg-gray-100 dark:bg-gray-800 rounded" />
            <div className="h-8 bg-gray-100 dark:bg-gray-800 rounded" />
          </div>
        </CardBody>
      )}

      {isError && (
        <CardBody>
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            Failed to load payment history.
          </p>
        </CardBody>
      )}

      {!isLoading && !isError && payments && payments.length === 0 && (
        <CardBody>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No payments recorded yet.</p>
        </CardBody>
      )}

      {!isLoading && !isError && payments && payments.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Date</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Method</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((p: PaymentHistoryItem) => (
              <TableRow key={p.id}>
                <TableCell>
                  {p.paidAt ? formatDate(new Date(p.paidAt)) : formatDate(new Date(p.createdAt))}
                </TableCell>
                <TableCell className="font-medium tabular-nums">
                  {formatPaymentAmount(p.amount)}
                </TableCell>
                <TableCell>{methodLabel(p.method)}</TableCell>
                <TableCell>
                  <PaymentStatusBadge status={p.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Record payment modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setRecordError(null);
        }}
        title="Record External Payment"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Record a payment received outside Stripe (e.g. bank transfer, cheque). Method will be
            saved as <strong>External payment</strong>.
          </p>
          <Input
            label="Amount (USD)"
            type="number"
            min={0.01}
            step={0.01}
            max={balanceDue && !isNaN(balanceNum) ? balanceNum : undefined}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            helperText={
              balanceDue && !isNaN(balanceNum)
                ? `Balance due: ${formatCurrency(balanceNum)}`
                : undefined
            }
          />
          <Input
            label="Date paid"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
          {recordError && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300"
            >
              {recordError}
            </div>
          )}
          <div className="flex gap-3 justify-end pt-1">
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setRecordError(null);
              }}
              disabled={recordPayment.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleRecord} isLoading={recordPayment.isPending}>
              Record Payment
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PaymentLinksSection
// ---------------------------------------------------------------------------

interface PaymentLinksSectionProps {
  invoiceId: string;
  canCreate: boolean;
  balanceDue?: string;
}

function PaymentLinksSection({ invoiceId, canCreate, balanceDue }: PaymentLinksSectionProps) {
  const { data: links, isLoading, isError } = usePaymentLinks(invoiceId);
  const createLink = useCreatePaymentLink();
  const revokeLink = useRevokePaymentLink();

  const [showCreate, setShowCreate] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealData, setRevealData] = useState<PaymentLinkCreateResponse | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null); // link id

  async function handleCreate() {
    setCreateError(null);
    try {
      const resp = await createLink.mutateAsync({ invoiceId, expiresInDays });
      setShowCreate(false);
      setExpiresInDays(30);
      setRevealData(resp);
    } catch (err: any) {
      const msg: string =
        err?.response?.data?.message ?? err?.message ?? 'Failed to create payment link.';
      setCreateError(msg);
    }
  }

  async function handleRevoke() {
    if (!revokeTarget) return;
    try {
      await revokeLink.mutateAsync({ linkId: revokeTarget, invoiceId });
    } finally {
      setRevokeTarget(null);
    }
  }

  const balanceNum = balanceDue !== undefined ? parseFloat(balanceDue) : NaN;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Payment Links</CardTitle>
            {balanceDue && !isNaN(balanceNum) && balanceNum > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                A new link will charge the balance due:{' '}
                <span className="font-medium text-gray-700 dark:text-gray-200">{formatCurrency(balanceNum)}</span>
              </p>
            )}
            {balanceDue && !isNaN(balanceNum) && balanceNum === 0 && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 font-medium">
                Invoice fully paid — no balance to collect.
              </p>
            )}
          </div>
          {canCreate && (
            <Button
              size="sm"
              leftIcon={<Link2 className="h-4 w-4" />}
              onClick={() => {
                setCreateError(null);
                setShowCreate(true);
              }}
            >
              Create payment link
            </Button>
          )}
        </div>
      </CardHeader>

      {isLoading && (
        <CardBody>
          <div
            className="space-y-2 animate-pulse"
            aria-busy="true"
            aria-label="Loading payment links"
          >
            <div className="h-8 bg-gray-100 dark:bg-gray-800 rounded" />
            <div className="h-8 bg-gray-100 dark:bg-gray-800 rounded" />
          </div>
        </CardBody>
      )}

      {isError && (
        <CardBody>
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            Failed to load payment links.
          </p>
        </CardBody>
      )}

      {!isLoading && !isError && links && links.length === 0 && (
        <CardBody>
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No payment links yet.</p>
            {canCreate && (
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Link2 className="h-4 w-4" />}
                onClick={() => {
                  setCreateError(null);
                  setShowCreate(true);
                }}
              >
                Create payment link
              </Button>
            )}
          </div>
        </CardBody>
      )}

      {!isLoading && !isError && links && links.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Created</TableHeader>
              <TableHeader>Expires</TableHeader>
              <TableHeader>Used On</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>
                <span className="sr-only">Actions</span>
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map((link) => {
              const badge = PAYMENT_LINK_BADGE[link.status];
              return (
                <TableRow key={link.id}>
                  <TableCell>{formatDate(new Date(link.createdAt))}</TableCell>
                  <TableCell>{formatDate(new Date(link.expiresAt))}</TableCell>
                  <TableCell>
                    {link.usedAt ? (
                      formatDate(new Date(link.usedAt))
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </TableCell>
                  <TableCell>
                    {link.status === 'active' && (
                      <button
                        type="button"
                        aria-label="Revoke payment link"
                        onClick={() => setRevokeTarget(link.id)}
                        className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
                        disabled={revokeLink.isPending && revokeTarget === link.id}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Revoke
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Create dialog — collects expiresInDays, then hands off to RevealModal */}
      <Modal
        isOpen={showCreate}
        onClose={() => {
          setShowCreate(false);
          setCreateError(null);
          setExpiresInDays(30);
        }}
        title="Create Payment Link"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="Expires in (days)"
            type="number"
            min={1}
            max={365}
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
            helperText="1–365 days from now"
          />
          {createError && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {createError}
            </p>
          )}
          <div className="flex gap-3 justify-end pt-1">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreate(false);
                setCreateError(null);
                setExpiresInDays(30);
              }}
              disabled={createLink.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              isLoading={createLink.isPending}
              disabled={expiresInDays < 1 || expiresInDays > 365}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* One-time reveal modal — handed the create response */}
      <PaymentLinkRevealModal reveal={revealData} onClose={() => setRevealData(null)} />

      {/* Revoke confirm */}
      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        title="Revoke Payment Link"
        message="Revoke this payment link? Anyone with the URL will no longer be able to pay."
        confirmLabel="Revoke"
        isLoading={revokeLink.isPending}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// InvoiceDetailPage
// ---------------------------------------------------------------------------

function InvoiceDetailPage() {
  const { invoiceId } = useParams({ from: '/invoices/$invoiceId' });
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const { data: invoice, isLoading, isError } = useInvoice(invoiceId);
  const voidInvoice = useVoidInvoice();
  const [showVoid, setShowVoid] = useState(false);

  async function handleVoid() {
    try {
      await voidInvoice.mutateAsync(invoiceId);
      setShowVoid(false);
    } catch {
      /* surfaced via mutation state */
    }
  }

  if (isLoading)
    return (
      <>
        <Header title="Invoice" subtitle="Loading..." />
        <PageContainer>
          <div className="space-y-4 animate-pulse" aria-busy="true" aria-label="Loading invoice">
            <div className="h-24 bg-gray-100 dark:bg-gray-800 rounded-xl" />
            <div className="h-48 bg-gray-100 dark:bg-gray-800 rounded-xl" />
          </div>
        </PageContainer>
      </>
    );

  if (isError || !invoice)
    return (
      <>
        <Header title="Invoice" />
        <PageContainer>
          <Card>
            <CardBody>
              <p className="text-red-600 dark:text-red-400" role="alert">
                {isError ? 'Failed to load invoice. Please try again.' : 'Invoice not found.'}
              </p>
              <Link
                to="/invoices"
                className="mt-3 inline-block text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Back to Invoices
              </Link>
            </CardBody>
          </Card>
        </PageContainer>
      </>
    );

  const canVoid =
    isAdmin && invoice.status !== InvoiceStatus.VOID && invoice.status !== InvoiceStatus.PAID;

  const canCreatePaymentLink =
    invoice.status !== InvoiceStatus.PAID && invoice.status !== InvoiceStatus.VOID;

  return (
    <>
      <Header
        title={`Invoice #${invoice.invoiceNumber}`}
        subtitle={(invoice as any).client?.name ?? ''}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/invoices"
              className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
            {canVoid && (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Ban className="h-4 w-4" />}
                onClick={() => setShowVoid(true)}
              >
                Void Invoice
              </Button>
            )}
          </div>
        }
      />
      <PageContainer>
        <div className="space-y-6">
          {/* Invoice summary */}
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</p>
                  <div className="mt-1">
                    <InvoiceStatusBadge status={invoice.status} />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(invoice.total)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Amount Paid</p>
                  <p className="mt-1 text-xl font-semibold text-green-700 dark:text-green-300">
                    {invoice.amountPaid !== undefined
                      ? formatCurrency(parseFloat(invoice.amountPaid))
                      : formatCurrency(0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Balance Due</p>
                  <p
                    className={`mt-1 text-xl font-bold ${
                      invoice.balanceDue !== undefined && parseFloat(invoice.balanceDue) === 0
                        ? 'text-green-700 dark:text-green-300'
                        : 'text-orange-600 dark:text-orange-400'
                    }`}
                  >
                    {invoice.balanceDue !== undefined
                      ? formatCurrency(parseFloat(invoice.balanceDue))
                      : formatCurrency(invoice.total)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Issued</p>
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
                    {formatDate(new Date(invoice.issuedAt))}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Due</p>
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
                    {formatDate(new Date(invoice.dueAt))}
                  </p>
                </div>
                {invoice.paidAt && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Paid</p>
                    <p className="mt-1 text-sm text-green-700 dark:text-green-300 font-medium">
                      {formatDate(new Date(invoice.paidAt))}
                    </p>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Line items */}
          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
            </CardHeader>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Description</TableHeader>
                  <TableHeader>Hours</TableHeader>
                  <TableHeader>Rate</TableHeader>
                  <TableHeader>Amount</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.lineItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{formatCurrency(item.rateSnapshot)}/hr</TableCell>
                    <TableCell className="font-medium">{formatCurrency(item.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <CardBody className="border-t border-gray-100 dark:border-gray-800">
              <div className="flex justify-end gap-8 text-sm">
                <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-end gap-8 text-sm mt-1">
                <span className="font-semibold text-gray-900 dark:text-gray-100">Total</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{formatCurrency(invoice.total)}</span>
              </div>
            </CardBody>
          </Card>

          {/* Payments — history + record external */}
          <PaymentsSection
            invoiceId={invoiceId}
            isAdmin={isAdmin}
            balanceDue={invoice.balanceDue}
            invoiceStatus={invoice.status}
          />

          {/* Payment links — balance-aware */}
          <PaymentLinksSection
            invoiceId={invoiceId}
            canCreate={canCreatePaymentLink}
            balanceDue={invoice.balanceDue}
          />
        </div>
      </PageContainer>

      <ConfirmDialog
        isOpen={showVoid}
        onClose={() => setShowVoid(false)}
        onConfirm={handleVoid}
        title="Void Invoice"
        confirmLabel="Void Invoice"
        isLoading={voidInvoice.isPending}
        message={`Are you sure you want to void invoice #${invoice.invoiceNumber}? This cannot be undone.`}
      />
    </>
  );
}
